import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Separator,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState } from '@repo/shared-client/form'
import { createFileRoute } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import AccountChangePassword from '@web/src/features/account/account-change-password/account-change-password'
import AccountDeleteModal from '@web/src/features/account/account-delete-modal/account-delete-modal'
import AccountMembership from '@web/src/features/account/account-membership/account-membership'
import AccountNSFW from '@web/src/features/account/account-nsfw/account-nsfw'
import { useAuth } from '@web/src/hooks/use-auth'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileNameField from 'src/components/primatives/sf-profile-name-field/sf-profile-name-field'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import { UPDATE_PROFILE_NAME } from 'src/lib/mutations'
import type { FindAccountEditGeneralQuery, FindAccountEditGeneralQueryVariables } from 'types/graphql'

const ACCOUNT_EDIT_GENERAL_QUERY: TypedDocumentNode<FindAccountEditGeneralQuery, FindAccountEditGeneralQueryVariables> =
  gql`
  query FindAccountEditGeneral($profileId: String!) {
    profile: profile(id: $profileId) {
      ...ProfileInfo
      lastNameChange
      uniqueId
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

type AccountProfile = NonNullable<FindAccountEditGeneralQuery['profile']>

const ProfileNameChange = ({
  profile,
  handleRefetch,
}: {
  profile: AccountProfile
  handleRefetch: () => Promise<unknown>
}) => {
  const { t } = useTranslation()
  const { hasPermission } = useAuth()
  const [showSpinner, setShowSpinnerValue] = useState(false)

  const allowChange =
    hasPermission('profile:update:all') ||
    (profile.lastNameChange ? dayjs(new Date()).diff(profile.lastNameChange, 'days') >= 30 : true)
  const formMethods = useForm({
    defaultValues: {
      profileName: profile.name,
    },
  })

  const { isDirty } = useFormState({ control: formMethods.control })

  const [updateProfileName] = useMutation(UPDATE_PROFILE_NAME, {
    onCompleted: () => {
      toast.success(t('Profile Name Changed'))
      handleRefetch()
      formMethods.reset()
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const onSubmit = (fieldData: { profileName: string }) => {
    setShowSpinnerValue(true)
    updateProfileName({
      variables: {
        id: profile.id,
        name: fieldData.profileName,
      },
    })
  }

  useEffect(() => {
    formMethods.reset({
      profileName: profile.name,
    })
  }, [profile.name, formMethods])

  const daysUntilRename = profile.lastNameChange ? 30 - dayjs(new Date()).diff(profile.lastNameChange, 'days') : 0

  return (
    <Card>
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <CardHeader className="gap-2">
          <CardTitle>{t('Change Profile Name')}</CardTitle>
          <Separator />
        </CardHeader>
        <CardContent className="flex w-full flex-col gap-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <SFProfileNameField
                defaultValue={profile.name}
                disabled={!allowChange}
                helperText={
                  allowChange
                    ? undefined
                    : t(
                        'You can only change your profile name once every 30 days. You can change your name in {{days}} days.',
                        { days: daysUntilRename }
                      )
                }
                name="profileName"
              />
            </div>
          </div>
          <div className="flex w-full justify-end gap-2">
            {isDirty ? (
              <Button onClick={() => formMethods.reset()} type="button" variant="outline">
                {t('Cancel')}
              </Button>
            ) : null}
            <Button disabled={!isDirty || showSpinner} type="submit">
              {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
              {t('Save')}
            </Button>
          </div>
        </CardContent>
      </Form>
    </Card>
  )
}

const DeleteAccount = ({ profile }: { profile: AccountProfile }) => {
  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)

  return (
    <Card className="border-destructive/40 bg-destructive/10">
      <CardHeader>
        <CardTitle>{t('Danger Zone')}</CardTitle>
        <Separator />
      </CardHeader>
      <CardContent>
        <p className="py-4 text-foreground">
          {t('Deleting your account, profile, and media will be immediate. Please make sure you want to do this.')}
        </p>
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={() => setDeleteModal(true)} type="button" variant="destructive">
          {t('Delete Your Account')}
        </Button>
      </CardFooter>

      <AccountDeleteModal onOpenChange={(e) => setDeleteModal(e.open)} open={deleteModal} profileId={profile.id} />
    </Card>
  )
}

export const Route = createFileRoute('/(auth)/(account-layout)/account/$profileId/general')({
  component: EditGeneralPage,
  head: () => ({
    meta: [
      { title: `Account General | ${env.VITE_APP_NAME}` },
      { name: 'description', content: 'Account General' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  }),
  loader: ({ context, params }) => {
    const queryRef = context.preloadQuery(ACCOUNT_EDIT_GENERAL_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function EditGeneralPage() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<FindAccountEditGeneralQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const handleRefetch = () => {
    return apolloClient.refetchQueries({ include: [ACCOUNT_EDIT_GENERAL_QUERY] })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.profile) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="ml-[200px] pt-3 text-destructive">{t('No Data')}</p>
        </CardContent>
      </Card>
    )
  }

  const profile = data.profile as AccountProfile

  return (
    <div className="flex w-full flex-col items-stretch gap-4">
      <AccountMembership />
      {/* <AccountBetaProgram /> */}
      <ProfileNameChange handleRefetch={handleRefetch} profile={profile} />
      <AccountNSFW profileId={profile.id} />
      <AccountChangePassword />
      <DeleteAccount profile={profile} />
    </div>
  )
}
